import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  TextField,
  Slider,
  IconButton,
} from '@mui/material';
import { Clear } from '@mui/icons-material';
import { useGetAvailableCountries } from '../../queries';

interface NimbuxHotelListFiltersProps {
  address: string;
  distance: number;
  orderBy: string;
  search: string;
  selectedCountry: string;
  onSearchChange: (value: string) => void;
  onOrderByChange: (value: string) => void;
  onSelectedCountryChange: (value: string) => void;
  onAddressChange: (value: string) => void;
  onDistanceChange: (value: number) => void;
}

interface FilterCageProps {
  title: string;
  children?: JSX.Element;
  onClear?: () => void;
  showClear?: boolean;
}

const FilterCage = ({ title, children, onClear, showClear }: FilterCageProps) => (
  <Box display="flex" py={1} flexDirection="column" alignItems="flex-start" alignSelf="stretch" gap={1}>
    <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
      <Typography variant="subtitle2">{title}:</Typography>
      {showClear && onClear && (
        <IconButton
          size="small"
          onClick={onClear}
          sx={{
            p: 0.5,
            color: 'text.secondary',
            '&:hover': {
              color: 'text.primary',
              backgroundColor: 'action.hover',
            },
          }}
        >
          <Clear fontSize="small" />
        </IconButton>
      )}
    </Box>
    {children}
  </Box>
);

export const NimbuxHotelListFilters = ({
  search,
  onSearchChange,
  orderBy,
  onOrderByChange,
  selectedCountry,
  onSelectedCountryChange,
  address,
  onAddressChange,
  distance,
  onDistanceChange,
}: NimbuxHotelListFiltersProps) => {
  const { t } = useTranslation();

  const { data: availableCountries } = useGetAvailableCountries();

  const countries = availableCountries?.map((country) => ({ code: country, name: country }));

  const [nameSearch, setNameSearch] = useState(search);
  const [countrySearch, setCountrySearch] = useState(selectedCountry);
  const [addressSearch, setAddressSearch] = useState(address);
  const [distanceSearch, setDistanceSearch] = useState(distance);

  const handleClearSearchByName = () => {
    setNameSearch('');
  };

  const handleClearCountry = () => {
    setCountrySearch('');
  };

  const handleClearAddress = () => {
    setAddressSearch('');
    setDistanceSearch(50);
  };

  useEffect(() => {
    onSearchChange(nameSearch);
    onSelectedCountryChange(countrySearch);
    onAddressChange(addressSearch);
    onDistanceChange(distanceSearch);
  }, [nameSearch, countrySearch, addressSearch, distanceSearch]);

  return (
    <Box width={384} display="flex" flexDirection="column" gap={1} p={2}>
      {/* Search by Name Section */}
      <FilterCage
        title={t('hotelContent.filters.searchByName')}
        onClear={handleClearSearchByName}
        showClear={Boolean(nameSearch?.trim())}
      >
        <Box mt={1} width="100%">
          <TextField
            size="small"
            fullWidth
            onChange={(e) => setNameSearch(e.target.value)}
            value={nameSearch}
            placeholder={t('hotelContent.filters.searchPlaceholder')}
          />
        </Box>
      </FilterCage>

      {/* Order Section */}
      <FilterCage title={t('hotelContent.filters.orderBy')}>
        <FormControl component="fieldset" size="small" sx={{ width: '100%' }}>
          <RadioGroup value={orderBy} onChange={(e) => onOrderByChange(e.target.value)}>
            <FormControlLabel value="name" control={<Radio size="small" />} label={t('hotelContent.filters.name')} />
            <FormControlLabel
              value="country"
              control={<Radio size="small" />}
              label={t('hotelContent.filters.country')}
            />
          </RadioGroup>
        </FormControl>
      </FilterCage>

      {/* Filter By Country Section */}
      <FilterCage
        title={t('hotelContent.filters.filterByCountry')}
        onClear={handleClearCountry}
        showClear={Boolean(countrySearch)}
      >
        <Box mt={1} width="100%">
          <FormControl size="small" fullWidth>
            <InputLabel shrink={true}>{t('hotelContent.filters.selectCountry')}</InputLabel>
            <Select
              value={countrySearch}
              defaultValue=""
              onChange={(e) => onSelectedCountryChange(e.target.value)}
              label={t('hotelContent.filters.selectCountry')}
            >
              <MenuItem value="">
                <em>{t('hotelContent.filters.allCountries')}</em>
              </MenuItem>
              {countries?.map((country) => (
                <MenuItem key={country.code} value={country.code}>
                  {country.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </FilterCage>

      {/* Filter by Address Section */}
      <FilterCage
        title={t('hotelContent.filters.filterByAddress')}
        onClear={handleClearAddress}
        showClear={Boolean(addressSearch.trim() || distanceSearch !== 50)}
      >
        <Box display="flex" flexDirection="column" mt={1} gap={2} sx={{ width: '100%' }}>
          {/* Address Input */}
          <TextField
            size="small"
            fullWidth
            label={t('hotelContent.filters.address')}
            value={addressSearch}
            onChange={(e) => setAddressSearch(e.target.value)}
            placeholder={t('hotelContent.filters.addressPlaceholder')}
          />

          {/* Distance Slider */}
          <Box>
            <Typography variant="body2" gutterBottom>
              {t('hotelContent.filters.distance')}: {distanceSearch} km
            </Typography>
            <Slider
              value={distanceSearch}
              onChange={(_, newValue) => setDistanceSearch(newValue as number)}
              min={1}
              max={120}
              step={1}
              marks={[
                { value: 1, label: '1km' },
                { value: 60, label: '60km' },
                { value: 120, label: '120km' },
              ]}
              valueLabelDisplay="auto"
              size="small"
            />
          </Box>
        </Box>
      </FilterCage>
    </Box>
  );
};
